<template>
    <div>
        <div class="d-flex align-center flex-wrap">
            <daterange
                :start-date-label="$t('date_from')"
                :end-date-label="$t('date_to')"
                :_start-date="dateFrom"
                :_end-date="dateTo"
                same-date
                :single-line="false"
                :hide-details="false"
                class="flex-grow-0"
                :end-date-days-offset="730"
                content-class="date-field flex-grow-0"
                @change-start-date="changeStartDate"
                @change-end-date="changeEndDate"
            />
            <v-radio-group v-model="availabilityTableViewMode" inline>
                <v-radio value="AVAILABLE" :label="$t('actual_availability')" />
                <v-radio value="ALL">
                    <template v-slot:label>
                        <div class="text-no-wrap">
                            <span class="text-capitalize">{{ $t('all') }}</span>
                            &nbsp;(<span>{{ $t('allocated') }}</span>
                            &nbsp;/&nbsp;
                            <span class="text-primary">{{ $t('booked') }}</span>
                            &nbsp;/&nbsp;
                            <span class="text-success">{{ $t('available') }}</span>
                            )
                        </div>
                    </template>
                </v-radio>
            </v-radio-group>
        </div>
        <v-tabs v-model="tab" :mandatory="false" color="primary">
            <v-tab v-for="type in massEditTypes" :key="type" :value="type">
                {{ $t(`mass_edit_type.${type}`) }}
            </v-tab>
        </v-tabs>
        <v-tabs-window v-model="tab">
            <v-window-item v-for="type in massEditTypes" :key="type" :value="type">
                <AvailabilityMassEditForm
                    :mode="type"
                    :date-from="dateFrom"
                    :date-to="dateTo"
                    :availability="availability"
                    :contract="contract"
                    class="mt-3"
                    :sub-allotments="subAllotments"
                    :data-cy="`product-availability-mass-edit-form-${type}`"
                    @update="loadAvailability"
                    @create-suballotment="loadSubAllotments"
                />
            </v-window-item>
        </v-tabs-window>
        <v-skeleton-loader
            v-if="availabilityLoading || subAllotmentsLoading"
            transition="fade-transition"
            type="table"
        />
        <template v-else>
            <AvailabilityTableLegend class="my-5 float-right" />
            <AvailabilityTable
                v-if="allotmentInventory && hasAllotments(allotmentInventory)"
                :inventory="allotmentInventory"
                :dates="dates"
                :date-from="dateFrom"
                :date-to="dateTo"
                :sub-allotments="subAllotments"
            >
                <template v-slot:release>
                    <tr>
                        <td class="td-header text-caption pl-2">{{ $t('release') }}</td>
                        <td v-for="day in dates" :key="`${day}`" :class="dateCellCSSClass(day)" class="text-center">
                            <template v-if="gatAvailability(allotmentInventory.availabilityType, day, 'NETTO')">
                                {{ gatAvailability(allotmentInventory.availabilityType, day, 'NETTO').release }}
                                {{
                                    $t(
                                        'day',
                                        gatAvailability(allotmentInventory.availabilityType, day, 'NETTO').release
                                    )
                                }}
                            </template>
                        </td>
                    </tr>
                </template>
            </AvailabilityTable>
            <AvailabilityTable
                v-if="onRequestInventory && hasAllotments(onRequestInventory)"
                :inventory="onRequestInventory"
                :dates="dates"
                :date-from="dateFrom"
                :date-to="dateTo"
                :sub-allotments="subAllotments"
            />
        </template>
    </div>
</template>

<script setup lang="ts">
    import Daterange from '~/components/search/forms/Daterange.vue'
    import AvailabilityTable from '~src/components/products/AvailabilityTable.vue'
    import AvailabilityMassEditForm from '~src/components/products/AvailabilityMassEditForm.vue'
    import {gatAvailability, hasAnyAvailability} from '~/utils/availability'
    import AvailabilityTableLegend from '~/components/products/AvailabilityTableLegend.vue'
    import {contractServiceType} from '~/utils/types/types'

    const {$api} = useNuxtApp()

    const {contract} = defineProps<{
        contract: any
    }>()

    const dateFrom = ref(new Date())
    const dateTo = ref(new Date())
    dateTo.value.setMonth(dateTo.value.getMonth() + 1)

    const availability = ref()
    const availabilityLoading = ref(true)

    const tab = ref('none')
    const $route = useRoute()

    const subAllotments = ref()
    const subAllotmentsLoading = ref(true)

    onMounted(async () => {
        await loadAvailability()
        await loadSubAllotments()
    })

    async function loadAvailability(): Promise<void> {
        try {
            availabilityLoading.value = true
            availability.value = await $api.availability.get({
                contractId: contract.id || $route.params?.contractId,
                dateFrom: dateFrom.value,
                dateTo: dateTo.value,
            })
        } catch (error) {
            availability.value = null
        } finally {
            availabilityLoading.value = false
        }
    }

    async function loadSubAllotments(): Promise<void> {
        try {
            subAllotmentsLoading.value = true
            subAllotments.value = await $api.subAllotments.get({
                contractId: contract.id || $route.params?.contractId,
                active: true,
            })
        } catch (error) {
            subAllotments.value = null
        } finally {
            subAllotmentsLoading.value = false
        }
    }

    const dates = computed(() => {
        const dates = []
        const date = new Date(dateFrom.value)
        while (date <= dateTo.value) {
            dates.push(new Date(date))
            date.setDate(date.getDate() + 1)
        }
        return dates
    })

    const massEditTypes = computed(() => {
        if (contract.serviceType === contractServiceType.ownVisa) {
            return ['blockout', 'set_by_default']
        }

        return ['mass_edit', 'terminate_allotment', 'blockout', 'set_release', 'set_by_default']
    })

    function changeStartDate($event) {
        dateFrom.value = $event
        loadAvailability()
    }

    function changeEndDate($event) {
        dateTo.value = $event
        loadAvailability()
    }

    const allotmentInventory = computed(() => availability.value?.inventories.find(({name}) => name === 'Allotment'))
    const onRequestInventory = computed(() => availability.value?.inventories.find(({name}) => name === 'On Request'))

    function hasAllotments(inventory) {
        return (
            hasAnyAvailability(inventory.availabilityType, dates.value, 'NETTO') ||
            hasAnyAvailability(inventory.availabilityType, dates.value, 'FREE_SALE') ||
            hasAnyAvailability(inventory.availabilityType, dates.value, 'CUSTOMER')
        )
    }

    const availabilityTableViewMode = ref<'AVAILABLE' | 'ALL'>('AVAILABLE')
    provide('availabilityTableViewMode', availabilityTableViewMode)
</script>
